import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectedCampaignVM } from 'features/src/lib/donations/types/campaigns.models';
import { CampaignDetailModalComponent } from './campaign-detail-modal.component';
import { CampaignStoryCardComponent } from './campaign-story-card.component';

@Component({
  selector: 'don-selected-card',
  imports: [CommonModule, TranslateModule, CampaignDetailModalComponent, CampaignStoryCardComponent],
  templateUrl: './selected-card.component.html',
  styleUrls: ['./selected-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectedCardComponent implements OnChanges {
  private readonly donationShareUrl = 'https://crezcofoundation.org/donation-custom';
  private readonly mobileInstagramImageTimeoutMs = 12000;
  private readonly desktopInstagramImageTimeoutMs = 20000;

  @Input({ required: true }) campaign: SelectedCampaignVM | null = null;
  @Input() section: 'full' | 'intro' | 'progress' = 'full';
  @Input() donorsCount = 0;
  @ViewChild(CampaignStoryCardComponent) storyCard?: CampaignStoryCardComponent;

  progressPctAnimated = 0;
  isShareMenuOpen = false;
  copiedLink = false;
  isReadMoreOpen = false;
  preparingChannel: 'instagram' | 'facebook' | 'whatsapp' | null = null;

  constructor(
    public translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaign']) {
      this.animateProgress();
    }
  }

  get name(): string {
    if (!this.campaign) return this.translate.instant('CAMPAIGN.BrandAlt');
    return this.isSpanish ? this.campaign.nameEs : this.campaign.nameEn;
  }

  get description(): string {
    if (!this.campaign) return '';
    return this.isSpanish ? this.campaign.descriptionEs : this.campaign.descriptionEn;
  }

  get introDescription(): string {
    const raw = this.description?.trim();
    if (!raw) return '';

    const cleaned = raw.replace(/\s+/g, ' ');
    const sentence = cleaned.match(/^[^.!?]+[.!?]/)?.[0] ?? cleaned.match(/^[^.!?]+/)?.[0] ?? cleaned;

    return sentence.trim();
  }

  get currentAmount(): number {
    return this.toNumber(this.campaign?.currentAmount);
  }

  get targetAmount(): number {
    return this.toNumber(this.campaign?.targetAmount);
  }

  get remainingAmount(): number {
    return Math.max(0, this.targetAmount - this.currentAmount);
  }

  get progressPct(): number {
    if (this.targetAmount <= 0) return 0;
    return Math.max(0, Math.min(100, (this.currentAmount / this.targetAmount) * 100));
  }

  get percentCompleteRounded(): number {
    return Math.round(this.progressPct);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isReadMoreOpen) {
      this.closeReadMore();
    }
  }

  toggleShareMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isShareMenuOpen = !this.isShareMenuOpen;
  }

  openReadMore(): void {
    this.isReadMoreOpen = true;
    this.isShareMenuOpen = false;
    this.cdr.markForCheck();
  }

  closeReadMore(): void {
    this.isReadMoreOpen = false;
    this.cdr.markForCheck();
  }

  async shareOn(channel: 'instagram' | 'facebook' | 'whatsapp'): Promise<void> {
    if (this.preparingChannel) {
      return;
    }

    const shareUrl = this.buildShareUrl();
    const shareText = `${this.name} ${shareUrl}`;

    // Handle Facebook and WhatsApp with image generation (show preparing spinner)
    if (channel === 'facebook' || channel === 'whatsapp') {
      await this.setPreparingShare(channel);

      try {
        const timeoutMs = this.isMobileDevice()
          ? this.mobileInstagramImageTimeoutMs
          : this.desktopInstagramImageTimeoutMs;
        const imageFile = await this.buildShareImageFileWithTimeout(timeoutMs);

        if (!imageFile) {
          this.notifyInstagramImageRequired();
          // Fallback deep link for each channel
          if (channel === 'facebook') this.openFacebookDeepLink(shareUrl);
          if (channel === 'whatsapp') this.openWhatsAppDeepLink(shareText);
          return;
        }

        this.isShareMenuOpen = false;

        if (channel === 'whatsapp') {
          // Try native share first
          const shared = await this.shareCampaignImage(shareUrl, imageFile);
          if (!shared) {
            this.downloadImageFile(imageFile);
            await this.copyShareLinkSilently(shareUrl);
            this.openWhatsAppDeepLink(shareText);
          }
          return;
        }

        // Facebook: provide the generated image and open Facebook share fallback
        this.downloadImageFile(imageFile);
        await this.copyShareLinkSilently(shareUrl);
        this.openFacebookDeepLink(shareUrl);
        return;
      } finally {
      await this.setPreparingShare(null);
      }
    }

    // Default flow for other channels (e.g., Instagram and generic sharing)
    if (channel === 'instagram') {
      await this.setPreparingShare(channel);

      try {
        const timeoutMs = this.isMobileDevice()
          ? this.mobileInstagramImageTimeoutMs
          : this.desktopInstagramImageTimeoutMs;
        const imageFile = await this.buildShareImageFileWithTimeout(timeoutMs);

        if (!imageFile) {
          this.notifyInstagramImageRequired();
          return;
        }

        this.isShareMenuOpen = false;

        if (this.isMobileDevice()) {
          const shared = await this.shareInstagramImage(shareUrl, imageFile);
          if (shared) {
            return;
          }

          // If direct file sharing is unavailable, keep the generated file available locally.
          this.downloadImageFile(imageFile);
          await this.copyShareLinkSilently(shareUrl);
          this.openInstagramDeepLink();
          return;
        }

        this.downloadImageFile(imageFile);
        await this.copyShareLinkSilently(shareUrl);
        this.openInstagramDeepLink();
        return;
      } finally {
      await this.setPreparingShare(null);
      }
    }

    const imageFile = await this.buildShareImageFileWithTimeout(this.mobileInstagramImageTimeoutMs);

    const shared = await this.shareCampaignImage(shareUrl, imageFile);
    if (shared) {
      this.isShareMenuOpen = false;
      return;
    }

    if (imageFile) {
      this.downloadImageFile(imageFile);
    }

    await this.copyShareLinkSilently(shareUrl);
    this.openInstagramDeepLink();

    this.isShareMenuOpen = false;
  }

  async copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.buildShareUrl());
      this.copiedLink = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.copiedLink = false;
        this.cdr.markForCheck();
      }, 1500);
    } catch {
      this.copiedLink = false;
    }
  }

  formatUsd(value: number): string {
    return `USD ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
      Number.isFinite(value) ? value : 0
    )}`;
  }

  @HostListener('document:click', ['$event'])
  onGlobalClick(event: MouseEvent): void {
    if (!this.isShareMenuOpen) return;
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.isShareMenuOpen = false;
      this.cdr.markForCheck();
    }
  }

  private animateProgress(): void {
    this.progressPctAnimated = 0;
    this.cdr.markForCheck();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.progressPctAnimated = this.progressPct;
        this.cdr.markForCheck();
      });
    });
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const clean = value.replace(/,/g, '').replace(/\$/g, '').trim();
      const parsed = Number(clean);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private get isSpanish(): boolean {
    const locale = (this.translate.currentLang || this.translate.getDefaultLang() || 'en').toLowerCase();
    return locale.startsWith('es');
  }

  private buildShareUrl(): string {
    return this.donationShareUrl;
  }

  private async copyShareLinkSilently(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // No-op: deep-link flow still works without clipboard support.
    }
  }

  private openWhatsAppDeepLink(shareText: string): void {
    const encoded = encodeURIComponent(shareText);
    const appUrl = `whatsapp://send?text=${encoded}`;
    const webUrl = `https://wa.me/?text=${encoded}`;
    this.openDeepLinkWithFallback(appUrl, webUrl);
  }

  private openFacebookDeepLink(shareUrl: string): void {
    const webUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    const appUrl = `fb://facewebmodal?href=${encodeURIComponent(webUrl)}`;
    this.openDeepLinkWithFallback(appUrl, webUrl);
  }

  private openInstagramDeepLink(): void {
    if (!this.isMobileDevice()) {
      const desktopUrl = 'https://www.instagram.com/';
      const popup = window.open(desktopUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        window.location.href = desktopUrl;
      }
      return;
    }

    this.openDeepLinkWithFallback('instagram://story-camera', 'https://www.instagram.com/');
  }

  private openDeepLinkWithFallback(appUrl: string, webUrl: string): void {
    let appSwitchDetected = false;
    const onVisibilityChange = () => {
      if (document.hidden) {
        appSwitchDetected = true;
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange, { once: true });

    const tryWebFallback = () => {
      if (appSwitchDetected || document.hidden) {
        return;
      }

      const popup = window.open(webUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        window.location.href = webUrl;
      }
    };

    try {
      const hiddenLink = document.createElement('a');
      hiddenLink.href = appUrl;
      hiddenLink.rel = 'noopener noreferrer';
      hiddenLink.style.display = 'none';
      document.body.appendChild(hiddenLink);
      hiddenLink.click();
      setTimeout(() => hiddenLink.remove(), 1000);
    } catch {
      // Fall back to the web share URL when app protocol opening is blocked.
    }

    window.setTimeout(tryWebFallback, 1300);
  }

  private async shareInstagramImage(shareUrl: string, imageFile: File | null): Promise<boolean> {
    if (!('share' in navigator) || !navigator.share) {
      return false;
    }

    try {
      if (imageFile && navigator.canShare?.({ files: [imageFile] })) {
        await navigator.share({
          title: this.name,
          files: [imageFile],
        });
        return true;
      }

      await navigator.share({
        title: this.name,
        text: `${this.name} ${shareUrl}`,
        url: shareUrl,
      });
      return true;
    } catch {
      return false;
    }
  }

  private async shareCampaignImage(shareUrl: string, imageFile: File | null): Promise<boolean> {
    if (!('share' in navigator) || !navigator.share) {
      return false;
    }

    try {
      if (imageFile && navigator.canShare?.({ files: [imageFile] })) {
        await navigator.share({
          title: this.name,
          text: `${this.name} ${shareUrl}`,
          files: [imageFile],
        });
        return true;
      }

      await navigator.share({
        title: this.name,
        text: `${this.name} ${shareUrl}`,
        url: shareUrl,
      });
      return true;
    } catch {
      return false;
    }
  }

  private async buildShareImageFile(): Promise<File | null> {
    return this.storyCard?.buildShareImageFile?.() ?? null;
  }

  private async buildShareImageFileWithTimeout(timeoutMs: number): Promise<File | null> {
    return Promise.race([
      this.buildShareImageFile(),
      new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  }

  private async setPreparingShare(channel: 'instagram' | 'facebook' | 'whatsapp' | null): Promise<void> {
    this.preparingChannel = channel;
    this.cdr.markForCheck();
  }

  private notifyInstagramImageRequired(): void {
    window.alert(this.translate.instant('CAMPAIGN.ImageGenerationFailed'));
  }

  private downloadImageFile(file: File): void {
    const objectUrl = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = file.name;
    anchor.rel = 'noopener';
    anchor.click();

    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 0);
  }

  private isMobileDevice(): boolean {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
  }
}
